import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getCohortBySlug, cohortDetails, getCohortStatus } from '@/lib/data/cohorts';
import { getPublicBaseUrl } from '@/lib/publicBaseUrl';
import { 
  Calendar, 
  Clock, 
  Users, 
  Award, 
  CheckCircle2, 
  ArrowLeft, 
  ArrowRight 
} from 'lucide-react';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PriceDisplay } from '@/components/ui/PriceDisplay';

export function CohortDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const cohort = slug ? getCohortBySlug(slug) : undefined;

  if (!cohort) {
    return <NotFoundPage />;
  }

  const status = getCohortStatus(cohort);
  const baseUrl = getPublicBaseUrl();
  const pagePath = `cohorts/${cohort.slug}`;
  const canonicalUrl = `${baseUrl}/${pagePath}`;

  const title = `${cohort.certificationName} — ${cohort.monthYear} Cohort | FIVE LENS Enneagram`;
  const description = `${cohort.certificationName} cohort commencing ${cohort.monthYear}. ${cohort.description}`;

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `${cohort.certificationName} — ${cohort.monthYear} Cohort`,
    "description": cohort.description,
    "provider": {
      "@type": "Organization",
      "name": "FIVE LENS Enneagram",
      "url": "https://www.fivelensenneagram.com"
    },
    "educationalCredentialAwarded": `FIVE LENS ${cohort.certificationName}`,
    "timeRequired": "P8W",
    "courseMode": ["online", "blended"],
    "inLanguage": "en",
    "offers": [
      {
        "@type": "Offer",
        "price": cohort.slug?.includes('team') ? "18000" : "12000",
        "priceCurrency": "ZAR",
        "availability": status === 'Open' ? "https://schema.org/InStock" : (status === 'In progress' ? "https://schema.org/InStock" : "https://schema.org/Discontinued"),
        "url": canonicalUrl
      },
      {
        "@type": "Offer",
        "price": cohort.slug?.includes('team') ? "1350" : "900",
        "priceCurrency": "GBP",
        "availability": status === 'Open' ? "https://schema.org/InStock" : (status === 'In progress' ? "https://schema.org/InStock" : "https://schema.org/Discontinued"),
        "url": canonicalUrl
      },
      {
        "@type": "Offer",
        "price": cohort.slug?.includes('team') ? "1800" : "1200",
        "priceCurrency": "USD",
        "availability": status === 'Open' ? "https://schema.org/InStock" : (status === 'In progress' ? "https://schema.org/InStock" : "https://schema.org/Discontinued"),
        "url": canonicalUrl
      }
    ],
    ...(status === 'Open' ? {
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": "Blended",
        "startDate": cohort.startDate,
        "endDate": cohort.endDate,
        "location": {
          "@type": "VirtualLocation",
          "url": canonicalUrl
        }
      }
    } : {})
  };

  return (
    <main className="pt-24 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
      </Helmet>

      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-ground-deep pb-6">
          <Link
            to="/cohorts"
            className="inline-flex items-center gap-2 text-sm font-medium text-ink/70 hover:text-rule-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Upcoming Cohorts
          </Link>
          <Link
            to={cohort.certificationUrl || '/practitioner-certification'}
            className="inline-flex items-center gap-2 text-sm font-semibold text-rule-3 hover:text-ink transition-colors"
          >
            View Full {cohort.certificationName} Curriculum
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Hero Section */}
        <div className="bg-white border border-ground-deep shadow-sm p-8 md:p-12 mb-12">
          <div className="max-w-3xl">
            <span className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest mb-4 border ${
              status === 'Open' ? 'bg-rule-3/10 text-rule-3 border-rule-3/20' :
              status === 'In progress' ? 'bg-amber-100 text-amber-800 border-amber-300' :
              'bg-gray-100 text-gray-700 border-gray-300'
            }`}>
              {status === 'Open' ? `Confirmed intake · ${cohort.monthYear}` : status === 'In progress' ? 'In progress' : 'Finished'}
            </span>
            <h1 className="text-3xl md:text-5xl font-serif text-ink mb-6">
              {cohort.certificationName}
            </h1>
            <p className="text-lg text-ink/80 font-light leading-relaxed mb-8">
              {cohort.description}
            </p>

            {status === 'Open' ? (
              <div className="flex flex-wrap gap-4 items-center">
                <Link
                  to={`/register?product=${cohort.certificationUrl?.includes('team') ? 'team-facilitator-certification' : 'practitioner-certification'}`}
                  className="bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ink/80 transition-colors inline-flex items-center gap-2"
                >
                  Enrol in this Cohort
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to={cohort.certificationUrl || '/practitioner-certification'}
                  className="bg-ground text-ink border border-ground-deep px-6 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ground-deep transition-colors"
                >
                  Learn More About Certification
                </Link>
              </div>
            ) : status === 'In progress' ? (
              <div className="p-4 bg-amber-50 border border-amber-200 text-amber-900 text-sm max-w-xl">
                <p className="font-semibold mb-1">This cohort has started.</p>
                <p>See <Link to="/cohorts" className="underline font-semibold text-rule-3">upcoming cohorts</Link> for available intakes.</p>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 border border-gray-200 text-gray-700 text-sm max-w-xl">
                <p className="font-semibold mb-1">This cohort has finished.</p>
                <p>See <Link to="/cohorts" className="underline font-semibold text-rule-3">upcoming cohorts</Link> for available intakes.</p>
              </div>
            )}
          </div>
        </div>

        {/* Cohort Key Details Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white p-8 border border-ground-deep shadow-sm">
            <Calendar className="w-8 h-8 text-rule-3 mb-4" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-ink/60 mb-2">Schedule & Dates</h3>
            <p className="text-lg font-serif text-ink font-bold mb-1">{cohort.monthYear}</p>
            <p className="text-sm font-semibold text-rule-3 mb-2">Starts {cohort.displayDate}</p>
            <p className="text-sm text-ink/70 leading-relaxed mb-4">{cohort.schedule}</p>
            {cohort.sessionDates && cohort.sessionDates.length > 0 && (
              <div className="text-xs text-ink/60 border-t border-ground-deep pt-3">
                <span className="font-semibold text-ink block mb-1">Live Zoom Dates:</span>
                <ul className="space-y-1">
                  {cohort.sessionDates.map((date, idx) => (
                    <li key={idx} className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-rule-3 shrink-0" />
                      <span>{date}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-white p-8 border border-ground-deep shadow-sm">
            <Clock className="w-8 h-8 text-rule-3 mb-4" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-ink/60 mb-2">Format & Workload</h3>
            <p className="text-lg font-serif text-ink font-bold mb-2">{cohort.format?.split('(')[0]}</p>
            <p className="text-sm text-ink/70 leading-relaxed mb-4">{cohort.format}</p>
            {cohort.icfCredits && (
              <div className="text-xs text-ink/80 bg-ground p-3 border border-ground-deep font-medium flex items-center gap-2">
                <Award className="w-4 h-4 text-rule-3 shrink-0" />
                <span>Accreditation: {cohort.icfCredits}</span>
              </div>
            )}
          </div>

          <div className="bg-white p-8 border border-ground-deep shadow-sm flex flex-col justify-between">
            <div>
              <Users className="w-8 h-8 text-rule-3 mb-4" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-ink/60 mb-2">Cohort Size & Faculty</h3>
              <p className="text-lg font-serif text-ink font-bold mb-2">Max {cohort.spotsAvailable || 15} Delegates</p>
              <p className="text-sm text-ink/70 leading-relaxed mb-4">
                Led by {cohort.faculty || 'FIVE LENS Faculty'} to guarantee personalized feedback and interactive case supervision.
              </p>
            </div>
            <div className="border-t border-ground-deep pt-4">
              <div className="text-2xl font-serif font-bold text-ink mb-1 min-h-[2.5rem] flex items-center">
                <PriceDisplay productKey={cohort.slug?.includes('team') ? 'team-facilitator-certification' : 'practitioner-certification'} />
              </div>
              <p className="text-xs text-ink/60">Standard tuition per delegate. Payment plans available.</p>
            </div>
          </div>
        </div>

        {/* Other Cohorts Navigation */}
        <div className="p-8 bg-ground border border-ground-deep text-center">
          <h3 className="text-xl font-serif text-ink mb-3">Explore Other Upcoming Intakes</h3>
          <p className="text-sm text-ink/70 mb-6 max-w-xl mx-auto">
            Looking for a different month or qualification level? View all upcoming intake dates across Practitioner and Team Facilitator certifications.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {cohortDetails.map((other) => (
              <Link
                key={other.slug}
                to={`/cohorts/${other.slug}`}
                className={`px-5 py-3 text-xs font-bold uppercase tracking-wider border transition-colors ${
                  other.slug === cohort.slug
                    ? 'bg-ink text-white border-ink pointer-events-none'
                    : 'bg-white text-ink border-ground-deep hover:border-rule-3'
                }`}
              >
                {other.certificationName} ({other.monthYear})
              </Link>
            ))}
            <Link
              to="/cohorts"
              className="px-5 py-3 text-xs font-bold uppercase tracking-wider bg-rule-3 text-white border border-rule-3 hover:bg-ink hover:border-ink transition-colors"
            >
              All Cohorts Overview
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

export default CohortDetailPage;
